import { useCallback } from 'react'

import { AppStatus } from '@/redux'
import { analytics } from '@/services/analytics/segment'
import { rdt } from '@/services/analytics/reddit'
import { QueryKeys } from '@/services/api/session/queryKeys'
import { useCreateProfile } from '@/services/api/session/useProfile'
import type { Profile } from '@/services/api/session/views'

import { ProviderId } from '@/features/Auth/providers/types'
import { signIn } from '@/features/Auth/session/signIn'
import { authStore } from '@/features/Auth/store/authStore'
import { ERRORS } from '@/features/Auth/session/views'

/**
 * Creating the profile this server is missing for an identity.
 *
 * The credential half is gone from here: obtaining one — creating the Firebase account, or
 * recovering the one an abandoned signup left behind — belongs to the e-mail provider, and
 * every other source arrives already holding a session. What is left is the part that was
 * always this hook's job, which is the profile.
 *
 * The machine is told when the profile exists, so a signup and a social sign-in end in the
 * same state by the same event. See EPIC-005.
 *
 * Registration is gated on the identity, never on the address. This used to ask
 * `check_email/` whether a profile already held the address and refuse when one did, which
 * is a different question from the one being asked: the identity being registered is
 * resolved by uid, and `Profile.email` is neither unique nor an identity. The two disagreed
 * for exactly the person EPIC-004 describes — a row whose `id` predates the shared pool, or
 * came from the `uuid.uuid4` default, so it matches no live identity. Signing in produced a
 * valid uid this server had never seen, the flow correctly routed them to registration, and
 * the address check then refused it: told to provide more details, then told the account
 * already existed, with no way out of either. EPIC-004 rules that such a row stays dormant
 * and that two rows may share an address — which is what not asking here allows.
 *
 * The check that remains is the one on the identity, and `CreateProfileView` already is it:
 * `Profile.id` is the uid, so a second signup for the same person answers with the profile
 * they already have rather than colliding. Asking first added nothing that the create does
 * not answer.
 */
export function useSignup() {
  const createProfile = useCreateProfile()

  const signup = useCallback(
    async (data: Partial<Profile>, source: ProviderId = 'email') => {
      try {
        AppStatus.set('loading')

        if (source === 'email') {
          await signIn('email', { email: data?.email, password: data?.password }, authStore.dispatch)

          if (authStore.get().status === 'failed') {
            // Reported already: `useAuthFlow` renders every failure this feature produces,
            // and the provider's own words — "that address already belongs to another
            // account" — say more than a second alert raised from here. Throwing only stops
            // a profile being created for a session that was never established.
            throw ERRORS.credentialFailed
          }
        }

        const user = await createProfile.mutateAsync(data)

        QueryKeys.me.setData([], user)
        authStore.dispatch({ type: 'profileCreated', uid: user?.id })

        analytics.track('sign_up', {
          sign_up_source: source,
          email: data?.email,
          verification_link: user?.email_verification_link
        })
        rdt('SignUp')
        analytics.track('login', { email: data?.email })

        return { user }
      } catch (e) {
        AppStatus.set('idle')
        logger.error('Error on signup', e, 'Auth')

        return { user: null }
      }
    },
    [createProfile]
  )

  return { signup }
}
